/**
 * Утилита для API-запросов с автоматическим добавлением basePath.
 * 
 * Next.js basePath НЕ подставляется к fetch() URL автоматически.
 * Эта функция решает проблему: при доступе через Nginx reverse proxy
 * запросы fetch('/api/...') уходят на корневой путь без /tasks.
 * 
 * Использование: заменить fetch('/api/...') на apiFetch('/api/...')
 */

// Кешируем basePath, чтобы не вычислять его каждый раз
let cachedBasePath: string | undefined

export function getBasePath(): string {
  if (cachedBasePath !== undefined) {
    return cachedBasePath as string
  }

  if (typeof window !== 'undefined') {
    // Способ 1: __NEXT_DATA__ (Pages Router, production App Router)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nextData = (window as any).__NEXT_DATA__
    if (nextData && typeof nextData.basePath === 'string') {
      cachedBasePath = nextData.basePath
      return cachedBasePath as string
    }

    // Способ 2: Извлечь basePath из текущего pathname.
    // В App Router (dev/production) pathname начинается с basePath.
    // Например: pathname = '/tasks/login' → basePath = '/tasks'
    const pathname = window.location.pathname
    // Ищем совпадение с известными маршрутами приложения
    const knownRoutes = ['/login', '/workspaces', '/board/', '/api/', '/admin']
    for (const route of knownRoutes) {
      const idx = pathname.indexOf(route)
      if (idx > 0) {
        cachedBasePath = pathname.slice(0, idx)
        return cachedBasePath as string
      }
    }

    // Способ 3: Если pathname заканчивается на '/' или пустой после первого сегмента,
    // проверяем, совпадает ли первый сегмент с известным basePath.
    // Например: pathname = '/tasks/' или '/tasks' → basePath = '/tasks'
    const segments = pathname.split('/').filter(Boolean)
    if (segments.length >= 1) {
      const possibleBasePath = '/' + segments[0]
      // Проверяем, что скрипт или стили загружены с этим basePath
      const scripts = document.querySelectorAll('script[src]')
      for (const script of scripts) {
        const src = script.getAttribute('src') || ''
        if (src.startsWith(possibleBasePath + '/_next/')) {
          cachedBasePath = possibleBasePath
          return cachedBasePath as string
        }
      }
      // Проверяем link[rel=stylesheet]
      const links = document.querySelectorAll('link[href]')
      for (const link of links) {
        const href = link.getAttribute('href') || ''
        if (href.startsWith(possibleBasePath + '/_next/')) {
          cachedBasePath = possibleBasePath
          return cachedBasePath as string
        }
      }
    }
  }

  // Fallback: переменная окружения
  cachedBasePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
  return cachedBasePath as string
}

export function apiFetch(url: string, options?: RequestInit): Promise<Response> {
  const basePath = getBasePath()
  const fullUrl = basePath + url
  return fetch(fullUrl, options).then(res => {
    if (res.status === 401 && typeof window !== 'undefined') {
      const loginUrl = basePath + '/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search)
      window.location.href = loginUrl
    }
    return res
  })
}

/**
 * SWR fetcher — обёртка над apiFetch для использования с useSWR.
 * Автоматически добавляет basePath и парсит JSON-ответ.
 *
 * Использование:
 *   const { data } = useSWR('/api/boards', apiFetcher)
 */
export async function apiFetcher<T = unknown>(url: string): Promise<T> {
  const res = await apiFetch(url)
  if (!res.ok) {
    const error = new Error('API request failed')
    throw error
  }
  return res.json()
}
